/**
 * デバイス一覧ページ - カレンダー風レイアウト
 */

import React, { useState, useEffect, useCallback } from 'react';
import { naturalSortBy } from '../services/utils';
import { useNavigate } from 'react-router-dom';
import { deviceApi } from '../services/api';
import { Icon } from '../components/icons';
import { Button, Badge, Alert, Input } from '../components/ui';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '../components/ui';

const STATUS_CONFIG = {
  active: { label: 'Active', variant: 'success', color: '#22c55e' },
  registered: { label: 'Registered', variant: 'primary', color: '#3b82f6' },
  maintenance: { label: 'Maintenance', variant: 'warning', color: '#f59e0b' },
  retired: { label: 'Retired', variant: 'default', color: '#6b7280' },
  unknown: { label: 'Unknown', variant: 'default', color: '#94a3b8' },
};

export default function DeviceList() {
  const navigate = useNavigate();

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextToken, setNextToken] = useState(null);

  // フィルター
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleStatuses, setVisibleStatuses] = useState(new Set(['active', 'registered', 'maintenance', 'retired', 'unknown']));
  const [visibleTypes, setVisibleTypes] = useState(new Set());
  const [sortCol, setSortCol] = useState('thingName');
  const [sortDir, setSortDir] = useState('asc');

  // セクション展開状態
  const [statusExpanded, setStatusExpanded] = useState(true);
  const [typeExpanded, setTypeExpanded] = useState(true);

  const handleSort = (col) => {
    setSortDir(prev => col === sortCol ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortCol(col);
  };

  // データ取得
  const fetchDevices = useCallback(async (token = null) => {
    setLoading(true);
    setError(null);
    try {
      const params = { maxResults: '50' };
      if (token) params.nextToken = token;

      const data = await deviceApi.list(params);
      const newDevices = token
        ? naturalSortBy([...devices, ...data.devices], 'thingName')
        : naturalSortBy(data.devices, 'thingName');
      setDevices(newDevices);
      setNextToken(data.nextToken);

      // 初回ロード時にTypeフィルターを全選択
      if (!token && visibleTypes.size === 0) {
        const types = new Set(data.devices.map(d => d.thingTypeName).filter(Boolean));
        types.add('_none_'); // Typeなしも含める
        setVisibleTypes(types);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [devices, visibleTypes.size]);

  useEffect(() => {
    fetchDevices();
    // eslint-disable-next-line
  }, []);

  // フィルタリング
  const filteredDevices = devices.filter((d) => {
    // 検索クエリ
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchName = d.thingName?.toLowerCase().includes(q);
      const matchType = d.thingTypeName?.toLowerCase().includes(q);
      const matchAttr = Object.values(d.attributes || {}).some(
        (v) => v?.toLowerCase().includes(q)
      );
      if (!matchName && !matchType && !matchAttr) return false;
    }
    // ステータスフィルター
    const status = d.lifecycleStatus || 'unknown';
    if (!visibleStatuses.has(status)) return false;
    // Typeフィルター
    const type = d.thingTypeName || '_none_';
    if (!visibleTypes.has(type)) return false;
    return true;
  });

  // 統計
  const stats = devices.reduce(
    (acc, d) => {
      const s = d.lifecycleStatus || 'unknown';
      acc[s] = (acc[s] || 0) + 1;
      acc.total++;
      return acc;
    },
    { total: 0 }
  );

  // ソート
  const sortedDevices = [...filteredDevices].sort((a, b) => {
    let av = a[sortCol] ?? '';
    let bv = b[sortCol] ?? '';
    const cmp = typeof av === 'string'
      ? av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' })
      : av - bv;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // ユニークな ThingType 一覧
  const thingTypes = [...new Set(devices.map((d) => d.thingTypeName).filter(Boolean))].sort();

  // トグル関数
  const toggleStatus = (status) => {
    setVisibleStatuses(prev => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const toggleAllStatuses = () => {
    const allStatuses = Object.keys(STATUS_CONFIG);
    if (visibleStatuses.size === allStatuses.length) {
      setVisibleStatuses(new Set());
    } else {
      setVisibleStatuses(new Set(allStatuses));
    }
  };

  const toggleType = (type) => {
    setVisibleTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const toggleAllTypes = () => {
    const allTypes = [...thingTypes, '_none_'];
    if (visibleTypes.size === allTypes.length) {
      setVisibleTypes(new Set());
    } else {
      setVisibleTypes(new Set(allTypes));
    }
  };

  if (loading && devices.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col -m-6 h-screen">
      {/* サブタイトル */}
      <div className="px-6 pt-6 pb-4 flex-shrink-0 bg-white">
        <p className="text-sm text-slate-500">機体一覧・管理</p>
      </div>

      {/* メインコンテンツ（2カラム） */}
      <div className="flex flex-1 overflow-hidden">
      {/* 左サイドバー */}
      <div className="w-56 flex-shrink-0 bg-white overflow-y-auto flex flex-col">
        {/* 上部: 検索（線なし） */}
        <div className="flex-shrink-0">
          {/* 検索 */}
          <div className="p-3">
            <Input
              placeholder="検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<Icon name="search" className="w-4 h-4" />}
              iconRight={searchQuery && (
                <button
                  className="hover:text-slate-600"
                  onClick={() => setSearchQuery('')}
                >
                  <Icon name="x" className="w-4 h-4" />
                </button>
              )}
            />
          </div>
        </div>

        {/* 下部: 統計・フィルター（右側に線あり） */}
        <div className="flex-1 border-r border-slate-200">
        {/* 合計 */}
        <div className="px-3 py-2 border-b border-slate-100">
          <div className="text-center">
            <span className="text-2xl font-bold text-slate-700">{stats.total}</span>
            <span className="text-sm text-slate-500 ml-2">台</span>
          </div>
        </div>

        {/* ライフサイクルフィルター */}
        <div className="border-b border-slate-100">
          <button
            onClick={() => setStatusExpanded(!statusExpanded)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
          >
            <Icon
              name={statusExpanded ? "chevronDown" : "chevronRight"}
              className="w-4 h-4 text-slate-400"
            />
            <span className="text-sm font-medium text-slate-700">ライフサイクル</span>
          </button>

          {statusExpanded && (
            <div className="px-2 pb-2 space-y-0.5">
              {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                const isVisible = visibleStatuses.has(key);
                const count = stats[key] || 0;
                return (
                  <button
                    key={key}
                    onClick={() => toggleStatus(key)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${
                      isVisible ? 'hover:bg-gray-50' : 'opacity-40 hover:opacity-60'
                    }`}
                  >
                    <span
                      className="w-3 h-3 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: isVisible ? config.color : '#cbd5e1' }}
                    />
                    <span className={`text-sm flex-1 ${isVisible ? 'text-slate-700' : 'text-slate-400'}`}>
                      {config.label}
                    </span>
                    <span className="text-xs text-slate-400">{count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Typeフィルター */}
        <div className="border-b border-slate-100">
          <button
            onClick={() => setTypeExpanded(!typeExpanded)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
          >
            <Icon
              name={typeExpanded ? "chevronDown" : "chevronRight"}
              className="w-4 h-4 text-slate-400"
            />
            <span className="text-sm font-medium text-slate-700">Type</span>
          </button>

          {typeExpanded && (
            <div className="px-2 pb-2 space-y-0.5 max-h-48 overflow-y-auto">
              {thingTypes.map((type) => {
                const isVisible = visibleTypes.has(type);
                return (
                  <button
                    key={type}
                    onClick={() => toggleType(type)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${
                      isVisible ? 'hover:bg-gray-50' : 'opacity-40 hover:opacity-60'
                    }`}
                  >
                    <span
                      className="w-3 h-3 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: isVisible ? '#3b82f6' : '#cbd5e1' }}
                    />
                    <span className={`text-sm ${isVisible ? 'text-slate-700' : 'text-slate-400'}`}>
                      {type}
                    </span>
                  </button>
                );
              })}
              <button
                onClick={() => toggleType('_none_')}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${
                  visibleTypes.has('_none_') ? 'hover:bg-gray-50' : 'opacity-40 hover:opacity-60'
                }`}
              >
                <span
                  className="w-3 h-3 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: visibleTypes.has('_none_') ? '#94a3b8' : '#cbd5e1' }}
                />
                <span className={`text-sm italic ${visibleTypes.has('_none_') ? 'text-slate-500' : 'text-slate-400'}`}>
                  （なし）
                </span>
              </button>
            </div>
          )}
        </div>

        {/* アクション */}
        <div className="p-3">
          <Button
            variant="primary"
            className="w-full"
            onClick={() => navigate('/provision')}
          >
            <Icon name="plus" className="w-4 h-4" />
            新規登録
          </Button>
        </div>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div className="flex-1 flex flex-col bg-white overflow-hidden">
        {/* エラー表示 */}
        {error && (
          <div className="px-4 py-2">
            <Alert variant="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </div>
        )}

        {/* テーブル */}
        <div className="flex-1 overflow-auto">
          <Table className="table-auto">
            <TableHeader>
              <TableRow>
                <TableHead className="w-16 whitespace-nowrap">接続</TableHead>
                <TableHead
                  sortable
                  sortDirection={sortCol === 'thingName' ? sortDir : null}
                  onSort={() => handleSort('thingName')}
                >
                  機体名
                </TableHead>
                <TableHead
                  sortable
                  sortDirection={sortCol === 'thingTypeName' ? sortDir : null}
                  onSort={() => handleSort('thingTypeName')}
                >
                  Type
                </TableHead>
                <TableHead
                  sortable
                  sortDirection={sortCol === 'lifecycleStatus' ? sortDir : null}
                  onSort={() => handleSort('lifecycleStatus')}
                  className="whitespace-nowrap"
                >
                  ライフサイクル
                </TableHead>
                <TableHead
                  sortable
                  sortDirection={sortCol === 'lastConnectedAt' ? sortDir : null}
                  onSort={() => handleSort('lastConnectedAt')}
                  className="whitespace-nowrap"
                >
                  最終接続
                </TableHead>
                <TableHead className="whitespace-nowrap">FW Version</TableHead>
                <TableHead
                  sortable
                  sortDirection={sortCol === 'assigneeEmail' ? sortDir : null}
                  onSort={() => handleSort('assigneeEmail')}
                  className="whitespace-nowrap"
                >
                  担当者
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedDevices.length === 0 ? (
                <TableEmpty
                  message="機体が見つかりません"
                  icon={<Icon name="device" className="w-8 h-8" />}
                />
              ) : (
                sortedDevices.map((device) => {
                  const statusConfig = STATUS_CONFIG[device.lifecycleStatus] || STATUS_CONFIG.unknown;
                  // Fleet Indexing の接続状態を優先、なければDynamoDBのステータスを使用
                  const isConnected = device.connectivity?.connected ?? (device.connectionStatus === 'connected');
                  const connectivityTime = device.connectivity?.timestamp || device.lastConnectedAt;
                  const connectivityLabel = device.connectivity
                    ? (isConnected ? 'オンライン' : 'オフライン')
                    : (device.connectionStatus || '不明');
                  return (
                    <TableRow
                      key={device.thingName}
                      onClick={() => navigate(`/devices/${device.thingName}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <div
                            className={`w-3 h-3 rounded-full ${
                              device.connectivity
                                ? (isConnected ? 'bg-emerald-500' : 'bg-slate-400')
                                : 'bg-slate-300'
                            }`}
                            title={`${connectivityLabel}${connectivityTime ? ` (${new Date(connectivityTime).toLocaleString('ja-JP')})` : ''}`}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-slate-900">{device.thingName}</span>
                      </TableCell>
                      <TableCell>
                        {device.thingTypeName ? (
                          <Badge variant="primary">{device.thingTypeName}</Badge>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusConfig.variant}>
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {connectivityTime
                          ? new Date(connectivityTime).toLocaleString('ja-JP')
                          : '—'}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {device.firmwareVersion || '—'}
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm" title={device.assigneeEmail || undefined}>
                        {device.assigneeDisplayName || device.assigneeEmail || '-'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* ページネーション */}
        {nextToken && (
          <div className="flex justify-center py-3 border-t border-slate-200">
            <Button
              variant="secondary"
              onClick={() => fetchDevices(nextToken)}
              disabled={loading}
              loading={loading}
            >
              さらに読み込む
            </Button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
